import React, { useState, useEffect } from 'react';
import {
    Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    Badge, IconButton, Switch, Modal, ModalOverlay, ModalContent,
    ModalHeader, ModalFooter, ModalBody, ModalCloseButton, FormControl,
    FormLabel, Input, Select, NumberInput, NumberInputField, Grid, GridItem,
    useDisclosure, useToast, useColorModeValue, Breadcrumb, BreadcrumbItem,
    BreadcrumbLink, InputGroup, InputLeftElement, AlertDialog, AlertDialogBody,
    AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
    HStack, Tooltip, Spinner, Empty
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { MdEdit, MdDelete, MdAdd } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../../axios-client';
import FloatingPlusButton from '../../../components/FloatingPlusButton';

export default function GestPlansAdmin() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const toast = useToast();

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal state for Add/Edit
    const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
    const [isEditing, setIsEditing] = useState(false);
    const [currentPlanId, setCurrentPlanId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        price: 0,
        billing_cycle: 'monthly',
        max_accounts: 0,
        max_orders: 0,
        max_store_platforms: 0,
        max_shipping_companies: 0,
        max_products: 0,
        max_businesses: 1,
        is_active: true,
        visible: true,
    });
    const [submitting, setSubmitting] = useState(false);

    // Alert dialog state for Delete
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);
    const cancelRef = React.useRef();

    const cardBg = useColorModeValue('white', 'gray.700');
    const borderC = useColorModeValue('gray.200', 'gray.600');
    const hoverBg = useColorModeValue('gray.50', 'gray.600');
    const theadBg = useColorModeValue('gray.50', 'gray.800');

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const res = await axiosClient.get('/subscription-plans/');
            const data = res.data?.results || res.data || [];
            setPlans(Array.isArray(data) ? data : []);
        } catch (error) {
            toast({
                title: t('Erreur'),
                description: t("Erreur lors de la récupération des plans d'abonnement"),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenAddModal = () => {
        setIsEditing(false);
        setCurrentPlanId(null);
        setFormData({
            name: '',
            price: 0,
            billing_cycle: 'monthly',
            max_accounts: 0,
            max_orders: 0,
            max_store_platforms: 0,
            max_shipping_companies: 0,
            max_products: 0,
            max_businesses: 1,
            is_active: true,
            visible: true,
        });
        onModalOpen();
    };

    const handleOpenEditModal = (plan) => {
        setIsEditing(true);
        setCurrentPlanId(plan.id);
        setFormData({
            name: plan.name || '',
            price: plan.price || 0,
            billing_cycle: plan.billing_cycle || 'monthly',
            max_accounts: plan.max_accounts ?? 0,
            max_orders: plan.max_orders ?? 0,
            max_store_platforms: plan.max_store_platforms ?? 0,
            max_shipping_companies: plan.max_shipping_companies ?? 0,
            max_products: plan.max_products ?? 0,
            max_businesses: plan.max_businesses ?? 1,
            is_active: plan.is_active ?? true,
            visible: plan.visible ?? true,
        });
        onModalOpen();
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleNumberChange = (name, valueString) => {
        setFormData((prev) => ({
            ...prev,
            [name]: valueString === '' ? 0 : Number(valueString),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (isEditing) {
                await axiosClient.patch(`/subscription-plans/${currentPlanId}/`, formData);
                toast({
                    title: t('Succès'),
                    description: t("Plan d'abonnement modifié avec succès"),
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'bottom-right',
                });
            } else {
                await axiosClient.post('/subscription-plans/', formData);
                toast({
                    title: t('Succès'),
                    description: t("Plan d'abonnement créé avec succès"),
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'bottom-right',
                });
            }
            onModalClose();
            fetchPlans();
        } catch (error) {
            toast({
                title: t('Erreur'),
                description: error.response?.data?.detail || t("Une erreur est survenue"),
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'bottom-right',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleField = async (plan, field) => {
        const newValue = !plan[field];
        // Optimistic update
        setPlans((prev) =>
            prev.map((p) => (p.id === plan.id ? { ...p, [field]: newValue } : p))
        );
        try {
            await axiosClient.patch(`/subscription-plans/${plan.id}/`, { [field]: newValue });
            toast({
                title: t('Statut mis à jour'),
                status: 'success',
                duration: 2000,
                isClosable: true,
                position: 'bottom-right',
            });
        } catch (error) {
            // Revert on failure
            setPlans((prev) =>
                prev.map((p) => (p.id === plan.id ? { ...p, [field]: !newValue } : p))
            );
            toast({
                title: t('Erreur'),
                description: t("Impossible de modifier le statut"),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
    };

    const confirmDelete = (plan) => {
        setPlanToDelete(plan);
        setIsAlertOpen(true);
    };

    const handleDelete = async () => {
        if (!planToDelete) return;
        try {
            await axiosClient.delete(`/subscription-plans/${planToDelete.id}/`);
            toast({
                title: t('Supprimé'),
                description: t("Plan d'abonnement supprimé avec succès"),
                status: 'info',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
            fetchPlans();
        } catch (error) {
            toast({
                title: t('Erreur'),
                description: t("Impossible de supprimer ce plan"),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
        } finally {
            setIsAlertOpen(false);
            setPlanToDelete(null);
        }
    };

    const filteredPlans = plans.filter((p) =>
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.price?.toString().includes(searchTerm) ||
        p.billing_cycle?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatLimit = (val) => (val === 0 ? t('Illimité') : val);

    return (
        <Box position="relative" mb={5} p={{ base: 4, md: 6 }}>
            {/* Floating Plus Button */}
            <FloatingPlusButton tooltip={t("Ajouter un plan d'abonnement")} onClick={handleOpenAddModal} />

            {/* Breadcrumb */}
            <Flex mb={6}>
                <Breadcrumb fontStyle="normal" fontSize={{ base: "15px", sm: "16px", lg: "17px" }} color="gray.400">
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => navigate('/')}>{t("Accueil")}</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbItem isCurrentPage>
                        <BreadcrumbLink color="purple.500" fontWeight="semibold">{t("Plans d'abonnement")}</BreadcrumbLink>
                    </BreadcrumbItem>
                </Breadcrumb>
            </Flex>

            {/* Main Content Box */}
            <Flex
                px={6}
                py={6}
                bg={cardBg}
                rounded="2xl"
                direction="column"
                w="full"
                shadow="md"
                border="1px solid"
                borderColor={borderC}
            >
                {/* Header & Search Bar */}
                <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
                    <Box>
                        <Text fontSize="2xl" fontWeight="bold" bgGradient="linear(to-r, purple.500, blue.500)" bgClip="text">
                            {t("Gestion des Plans d'Abonnement")}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                            {t("Configurez les tarifs, cycles de facturation et limites des fonctionnalités SaaS.")}
                        </Text>
                    </Box>

                    <InputGroup size="md" w={{ base: 'full', md: '300px' }}>
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                            borderRadius="xl"
                            placeholder={t("Rechercher un plan...")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </InputGroup>
                </Flex>

                {/* Table Container */}
                {loading ? (
                    <Flex justify="center" align="center" py={12}>
                        <Spinner size="xl" color="purple.500" thickness="4px" />
                    </Flex>
                ) : (
                    <TableContainer overflowX="auto">
                        <Table variant="simple" size="md">
                            <Thead bg={theadBg}>
                                <Tr>
                                    <Th>{t("Nom & Cycle")}</Th>
                                    <Th isNumeric>{t("Prix")}</Th>
                                    <Th isNumeric>{t("Commandes")}</Th>
                                    <Th isNumeric>{t("Comptes")}</Th>
                                    <Th isNumeric>{t("Businesses")}</Th>
                                    <Th isNumeric>{t("APIs / Livraisons")}</Th>
                                    <Th textAlign="center">{t("Actif")}</Th>
                                    <Th textAlign="center">{t("Visible")}</Th>
                                    <Th textAlign="center">{t("Actions")}</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {filteredPlans.length === 0 ? (
                                    <Tr>
                                        <Td colSpan={9} textAlign="center" py={8} color="gray.500">
                                            {t("Aucun plan d'abonnement trouvé.")}
                                        </Td>
                                    </Tr>
                                ) : (
                                    filteredPlans.map((plan) => (
                                        <Tr key={plan.id} _hover={{ bg: hoverBg }} transition="0.2s">
                                            <Td>
                                                <Text fontWeight="bold" fontSize="md">{plan.name}</Text>
                                                <Badge
                                                    colorScheme={
                                                        plan.billing_cycle === 'yearly' ? 'purple' :
                                                        plan.billing_cycle === 'monthly' ? 'blue' : 'orange'
                                                    }
                                                    rounded="full"
                                                    px={2.5}
                                                    py={0.5}
                                                    fontSize="xs"
                                                >
                                                    {plan.billing_cycle === 'yearly' ? t('Annuel') :
                                                     plan.billing_cycle === 'monthly' ? t('Mensuel') : t('Par commande')}
                                                </Badge>
                                            </Td>
                                            <Td isNumeric fontWeight="semibold" color="purple.600" fontSize="md">
                                                {Number(plan.price).toLocaleString()} {t("MAD")}
                                            </Td>
                                            <Td isNumeric>
                                                <Badge colorScheme={plan.max_orders === 0 ? 'green' : 'gray'} px={2} py={1} rounded="md">
                                                    {formatLimit(plan.max_orders)}
                                                </Badge>
                                            </Td>
                                            <Td isNumeric>
                                                <Badge colorScheme={plan.max_accounts === 0 ? 'green' : 'gray'} px={2} py={1} rounded="md">
                                                    {formatLimit(plan.max_accounts)}
                                                </Badge>
                                            </Td>
                                            <Td isNumeric>
                                                <Badge colorScheme={plan.max_businesses === 0 ? 'purple' : 'gray'} px={2} py={1} rounded="md">
                                                    🏢 {formatLimit(plan.max_businesses)}
                                                </Badge>
                                            </Td>
                                            <Td isNumeric>
                                                <HStack justify="flex-end" spacing={1}>
                                                    <Tooltip label={t("APIs E-commerce (Shopify, WooCommerce...)")}>
                                                        <Badge colorScheme="blue" px={1.5} py={0.5} rounded="md">
                                                            🛒 {formatLimit(plan.max_store_platforms)}
                                                        </Badge>
                                                    </Tooltip>
                                                    <Tooltip label={t("APIs Livraison (Store EP, COD...)")}>
                                                        <Badge colorScheme="teal" px={1.5} py={0.5} rounded="md">
                                                            🚚 {formatLimit(plan.max_shipping_companies)}
                                                        </Badge>
                                                    </Tooltip>
                                                </HStack>
                                            </Td>
                                            <Td textAlign="center">
                                                <Switch
                                                    isChecked={plan.is_active}
                                                    onChange={() => handleToggleField(plan, 'is_active')}
                                                    colorScheme="green"
                                                />
                                            </Td>
                                            <Td textAlign="center">
                                                <Switch
                                                    isChecked={plan.visible}
                                                    onChange={() => handleToggleField(plan, 'visible')}
                                                    colorScheme="purple"
                                                />
                                            </Td>
                                            <Td textAlign="center">
                                                <HStack justify="center" spacing={2}>
                                                    <Tooltip label={t("Modifier")}>
                                                        <IconButton
                                                            icon={<MdEdit />}
                                                            size="sm"
                                                            colorScheme="blue"
                                                            variant="ghost"
                                                            onClick={() => handleOpenEditModal(plan)}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip label={t("Supprimer")}>
                                                        <IconButton
                                                            icon={<MdDelete />}
                                                            size="sm"
                                                            colorScheme="red"
                                                            variant="ghost"
                                                            onClick={() => confirmDelete(plan)}
                                                        />
                                                    </Tooltip>
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    ))
                                )}
                            </Tbody>
                        </Table>
                    </TableContainer>
                )}
            </Flex>

            {/* Modal Add / Edit Plan */}
            <Modal isOpen={isModalOpen} onClose={onModalClose} size="2xl" isCentered>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent rounded="2xl" p={2}>
                    <ModalHeader bgGradient="linear(to-r, purple.500, blue.500)" bgClip="text">
                        {isEditing ? t("Modifier le plan d'abonnement") : t("Créer un plan d'abonnement")}
                    </ModalHeader>
                    <ModalCloseButton />
                    <form onSubmit={handleSubmit}>
                        <ModalBody py={4}>
                            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                                <GridItem colSpan={{ base: 1, md: 2 }}>
                                    <FormControl isRequired>
                                        <FormLabel fontWeight="semibold">{t("Nom du plan")}</FormLabel>
                                        <Input
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder={t("ex: BASIC, PRO, ENTERPRISE")}
                                            rounded="xl"
                                        />
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl isRequired>
                                        <FormLabel fontWeight="semibold">{t("Prix (MAD)")}</FormLabel>
                                        <NumberInput min={0} value={formData.price} onChange={(val) => handleNumberChange('price', val)}>
                                            <NumberInputField rounded="xl" />
                                        </NumberInput>
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl isRequired>
                                        <FormLabel fontWeight="semibold">{t("Cycle de facturation")}</FormLabel>
                                        <Select
                                            name="billing_cycle"
                                            value={formData.billing_cycle}
                                            onChange={handleInputChange}
                                            rounded="xl"
                                        >
                                            <option value="monthly">{t("Mensuel (Monthly)")}</option>
                                            <option value="yearly">{t("Annuel (Yearly)")}</option>
                                            <option value="per_order">{t("Par commande (Per Order)")}</option>
                                        </Select>
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl>
                                        <FormLabel fontWeight="semibold">{t("Max Commandes (0 = Illimité)")}</FormLabel>
                                        <NumberInput min={0} value={formData.max_orders} onChange={(val) => handleNumberChange('max_orders', val)}>
                                            <NumberInputField rounded="xl" />
                                        </NumberInput>
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl>
                                        <FormLabel fontWeight="semibold">{t("Max Comptes Utilisateurs (0 = Illimité)")}</FormLabel>
                                        <NumberInput min={0} value={formData.max_accounts} onChange={(val) => handleNumberChange('max_accounts', val)}>
                                            <NumberInputField rounded="xl" />
                                        </NumberInput>
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl>
                                        <FormLabel fontWeight="semibold">{t("Max APIs E-commerce (0 = Illimité)")}</FormLabel>
                                        <NumberInput min={0} value={formData.max_store_platforms} onChange={(val) => handleNumberChange('max_store_platforms', val)}>
                                            <NumberInputField rounded="xl" />
                                        </NumberInput>
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl>
                                        <FormLabel fontWeight="semibold">{t("Max APIs Livraison (0 = Illimité)")}</FormLabel>
                                        <NumberInput min={0} value={formData.max_shipping_companies} onChange={(val) => handleNumberChange('max_shipping_companies', val)}>
                                            <NumberInputField rounded="xl" />
                                        </NumberInput>
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl>
                                        <FormLabel fontWeight="semibold">{t("Max Produits (0 = Illimité)")}</FormLabel>
                                        <NumberInput min={0} value={formData.max_products} onChange={(val) => handleNumberChange('max_products', val)}>
                                            <NumberInputField rounded="xl" />
                                        </NumberInput>
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl>
                                        <FormLabel fontWeight="semibold">{t("Max Businesses (0 = Illimité)")}</FormLabel>
                                        <NumberInput min={0} value={formData.max_businesses} onChange={(val) => handleNumberChange('max_businesses', val)}>
                                            <NumberInputField rounded="xl" />
                                        </NumberInput>
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl display="flex" alignItems="center" mt={4}>
                                        <FormLabel htmlFor="is_active" mb="0" fontWeight="semibold">
                                            {t("Plan Actif")}
                                        </FormLabel>
                                        <Switch
                                            id="is_active"
                                            name="is_active"
                                            isChecked={formData.is_active}
                                            onChange={handleInputChange}
                                            colorScheme="green"
                                        />
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl display="flex" alignItems="center" mt={4}>
                                        <FormLabel htmlFor="visible" mb="0" fontWeight="semibold">
                                            {t("Visible aux clients")}
                                        </FormLabel>
                                        <Switch
                                            id="visible"
                                            name="visible"
                                            isChecked={formData.visible}
                                            onChange={handleInputChange}
                                            colorScheme="purple"
                                        />
                                    </FormControl>
                                </GridItem>
                            </Grid>
                        </ModalBody>
                        <ModalFooter gap={3}>
                            <Button variant="outline" rounded="xl" onClick={onModalClose}>
                                {t("Annuler")}
                            </Button>
                            <Button
                                type="submit"
                                colorScheme="purple"
                                rounded="xl"
                                isLoading={submitting}
                                leftIcon={<MdEdit />}
                            >
                                {isEditing ? t("Enregistrer") : t("Créer")}
                            </Button>
                        </ModalFooter>
                    </form>
                </ModalContent>
            </Modal>

            {/* Alert Dialog Delete */}
            <AlertDialog isOpen={isAlertOpen} leastDestructiveRef={cancelRef} onClose={() => setIsAlertOpen(false)}>
                <AlertDialogOverlay>
                    <AlertDialogContent rounded="2xl">
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {t("Supprimer le plan")} : {planToDelete?.name}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {t("Êtes-vous sûr de vouloir supprimer ce plan d'abonnement ? Cette action est irréversible.")}
                        </AlertDialogBody>
                        <AlertDialogFooter gap={3}>
                            <Button ref={cancelRef} variant="outline" rounded="xl" onClick={() => setIsAlertOpen(false)}>
                                {t("Annuler")}
                            </Button>
                            <Button colorScheme="red" rounded="xl" onClick={handleDelete}>
                                {t("Supprimer")}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
}
